import {
  convertPrecipitationValue,
  convertPressureValue,
  convertTemperatureValue,
  convertWindSpeedValue,
} from './meshtasticWeatherUnits';
import { getObservationDisplayTimestampMs } from './meshtasticWeatherTimestamps';

const toNumber = (value) => value === null || value === undefined || value === ''
  ? null
  : Number.isFinite(Number(value)) ? Number(value) : null;

export function buildChartHistoryUrl(baseUrl, { stationId, fromIso, toIso, maxPoints }) {
  const url = new URL(baseUrl);
  ['sample', 'limit', 'order', 'nextToken'].forEach((key) => url.searchParams.delete(key));
  Object.entries({ stationId, from: fromIso, to: toIso, view: 'chart', maxPoints }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  });
  return url.toString();
}

export function getChartHistoryMeta(payload) {
  return {
    mode: payload?.mode || 'detail',
    resolution: payload?.resolution || 'raw',
    resolutionYears: toNumber(payload?.resolutionYears),
    maxPoints: toNumber(payload?.maxPoints),
    rawCount: toNumber(payload?.totalMatched),
    displayedCount: Array.isArray(payload?.items) ? payload.items.length : 0,
    from: payload?.from,
    to: payload?.to,
    coverage: payload?.coverage || null,
    updatedAt: payload?.updatedAt,
    timestampBasis: payload?.timestampBasis || 'display',
  };
}

export function getHistoryResolutionLabel(meta) {
  const labels = { hour: 'Hourly', day: 'Daily', month: 'Monthly', year: 'Yearly' };
  if (meta?.mode !== 'aggregate') return 'Individual readings';
  if (meta.resolution === 'year' && meta.resolutionYears > 1) return `${meta.resolutionYears}-year summaries`;
  return `${labels[meta.resolution] || 'Interval'} summaries`;
}

export function convertHistoryMetricValue(value, metric, units = {}) {
  const number = toNumber(value);
  if (number === null) return null;
  if (metric.kind === 'temperature') return convertTemperatureValue(number, units.temperatureUnit || 'c');
  if (metric.kind === 'wind') return convertWindSpeedValue(number, units.windSpeedUnit || 'ms');
  if (metric.kind === 'pressure') return convertPressureValue(number, units.pressureUnit || 'hpa');
  if (metric.kind === 'precipitation') return convertPrecipitationValue(number, units.precipitationUnit || 'mm');
  return number;
}

export function getHistoryMetricSummary(summary, metric, units) {
  const stats = metric.valueKeys.map((key) => summary?.metrics?.[key]).find(Boolean);
  const count = toNumber(stats?.count);
  const sum = toNumber(stats?.sum);
  const convert = (value) => convertHistoryMetricValue(value, metric, units);
  return {
    latestValue: convert(stats?.last),
    averageValue: count > 0 && sum !== null ? convert(sum / count) : null,
    minValue: convert(stats?.min),
    maxValue: convert(stats?.max),
    totalValue: metric.kind === 'precipitation' ? convert(summary?.rain?.totalMm) : null,
  };
}

export function nearestHistoryPointIndex(points, x) {
  if (!points.length) return -1;
  let low = 0;
  let high = points.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (points[middle].x < x) low = middle + 1;
    else high = middle;
  }
  return low > 0 && Math.abs(points[low - 1].x - x) <= Math.abs(points[low].x - x) ? low - 1 : low;
}

export function historyTimeDomain(points, meta) {
  const start = Date.parse(meta?.from);
  const end = Date.parse(meta?.to);
  const from = Number.isFinite(start) ? start : points[0]?.observedAtMs || 0;
  const to = Number.isFinite(end) ? end : points[points.length - 1]?.bucketEndMs || points[points.length - 1]?.observedAtMs || from + 1;
  return { from, to: Math.max(to, from + 1) };
}

const csvCell = (value) => {
  const text = value === undefined || value === null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function buildChartHistoryCsv(items, meta = {}) {
  const keys = [...new Set((items || []).flatMap((item) => Object.keys(item.weather || {})))].sort();
  const statKeys = [...new Set((items || []).flatMap((item) => Object.keys(item.stats || {})))].sort();
  const statFields = ['sum', 'count', 'min', 'max', 'last', 'lastAt'];
  const header = ['resolution', 'timestamp_basis', 'bucket_start_utc', 'bucket_end_utc', 'reading_count', ...keys,
    ...statKeys.flatMap((key) => statFields.map((field) => `${key}_${field}`))];
  const rows = (items || []).map((item) => [
    meta.resolution || 'raw', meta.timestampBasis || 'display',
    item.bucketStart || new Date(getObservationDisplayTimestampMs(item)).toISOString(), item.bucketEnd || '', item.count ?? 1,
    ...keys.map((key) => item.weather?.[key]),
    ...statKeys.flatMap((key) => statFields.map((field) => item.stats?.[key]?.[field])),
  ]);
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}
